<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { navItems } from '~/data/pages/microcredit/mocks'
import type { IResponseData } from '~/types'
import { WidgetOrganizationAsideContacts } from '~/components/widget/organization-aside-contacts'
import { WidgetBankServicesMenu } from '~/components/widget/bank'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { useSeoFromCustom } from '~/composables/useSeoFromCustom'
import { WidgetSeoContent } from '~/components/widget/seo'
import type { IMicroCredit } from '~/components/entity/microcredit/item'
import { WidgetAboutPageHeader } from '~/components/widget/about-page'
import { WidgetNewsListMeiliSearch } from '~/components/widget/news-list-meili-search'

const route = useRoute()
const { t, locale } = useI18n()
const url = `/microcredit/${route.params.microcredit}`
const { data } = await useApiWith404<IResponseData<IMicroCredit>>(() => url, {
  key: `microcredit-page-${route.params.slug}`
})
const microcredit = computed(() => data.value?.data)
useEmptySectionNoindex(microcredit, 'news_count')

const requestUrl = useRequestURL()
const baseUrl = requestUrl.origin

const seoData = computed(() => ({
  organization: {
    title: microcredit.value?.title
  }
}))

useSeoFromCustom(seoData, baseUrl, locale.value)

const breadcrumbs = computed(() => [
  { title: t('common.microcredits'), url: '/microcredit' },
  {
    title: microcredit.value?.title || t('common.microcredits'),
    url: `/microcredit/${route.params.microcredit}`
  },
  { title: t('common.news'), url: `/microcredit/${route.params.microcredit}/news` }
])
const fitOrganization = computed(() => {
  return {
    type: microcredit.value?.type || '',
    title: microcredit.value?.title || '',
    logo: microcredit.value?.logo,
    organization: {
      official_title: microcredit.value?.official_title || ''
    },
    views_count: microcredit.value?.views_count || 0,
    updated_at: microcredit.value?.updated_at || ''
  }
})
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetOrganizationAsideContacts
              v-if="microcredit"
              :organization="microcredit"
              :type="'microcredit'"
            />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
          </aside>
        </template>
        <div class="space-y-5">
          <WidgetAboutPageHeader
            :type="'microcredit'"
            :product="fitOrganization"
            :heading="t('common.news')"
          >
            <WidgetBankServicesMenu
              v-if="microcredit && microcredit.status === 'active'"
              :organization="microcredit"
              :nav-items="navItems"
              :type="'microcredit'"
            />
          </WidgetAboutPageHeader>
          <WidgetSeoContent
            heading-level="h2"
            heading-class="h3"
            leading-level="8"
            position="upper"
            :variables="{
              organization: microcredit?.official_title ?? ''
            }"
          />
          <WidgetNewsListMeiliSearch
            v-if="microcredit"
            :organization="microcredit"
            :query-params="{ per_page: '12' }"
          />
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
